import { useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Vista "Por Talento" del quiz Fortalezas 2.0.
 *
 * Entrada: summary.preguntas_talento = [{frase_id, frase, tipo_fortaleza,
 *   talentos: [{talento_id, talento, respuestas: [{sub_frase_id, texto, tipo, valor}]}]}]
 *
 * Se re-agrupa por talento → frase para mantener la UX del summary de confirmación.
 */
const PtTabPorTalento = ({ preguntas }) => {
  const [activeTabPerTalento, setActiveTabPerTalento] = useState({});

  const porTalento = useMemo(() => {
    const map = new Map();
    (preguntas || []).forEach((grupo) => {
      (grupo.talentos || []).forEach((t) => {
        if (!map.has(t.talento_id)) {
          map.set(t.talento_id, {
            talento_id: t.talento_id,
            talento: t.talento,
            frases: [],
          });
        }
        map.get(t.talento_id).frases.push({
          frase_id: grupo.frase_id,
          frase: grupo.frase,
          tipo_fortaleza: grupo.tipo_fortaleza,
          respuestas: t.respuestas || [],
        });
      });
    });
    return Array.from(map.values()).map((t) => ({
      ...t,
      total: t.frases.reduce((a, f) => a + f.respuestas.length, 0),
      total_si: t.frases.reduce(
        (a, f) => a + f.respuestas.filter((r) => r.valor).length,
        0
      ),
    }));
  }, [preguntas]);

  const getActive = (id) => activeTabPerTalento[id] || 0;
  const setActive = (id, idx) =>
    setActiveTabPerTalento((prev) => ({ ...prev, [id]: idx }));

  if (porTalento.length === 0) {
    return (
      <div className="cs-empty-state">
        <Icon icon="mdi:information-outline" />
        <span>No hay respuestas registradas.</span>
      </div>
    );
  }

  return (
    <div className="row">
      {porTalento.map((t, cardIndex) => {
        const activeFrase = getActive(t.talento_id);
        const frase = t.frases[activeFrase];

        return (
          <motion.div
            key={t.talento_id}
            className="col-lg-6 col-12 mb-20"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.1 + cardIndex * 0.08,
              duration: 0.4,
              ease: "easeOut",
            }}
          >
            <div className="card radius-12 h-100 cs-talent-card">
              <div className="card-body p-20">
                <div className="cs-talent-header">
                  <div className="d-flex align-items-center gap-2">
                    <div className="cs-talent-star-wrap">
                      <Icon icon="mdi:star" />
                    </div>
                    <h3 className="cs-talent-name mb-0">{t.talento}</h3>
                  </div>
                  <span className="cs-talent-badge">
                    {t.total_si}/{t.total} Sí
                  </span>
                </div>

                {t.frases.length > 0 && (
                  <div className="cs-tabs-wrap">
                    {t.frases.map((f, idx) => (
                      <button
                        key={f.frase_id}
                        className={`cs-tab-btn ${
                          activeFrase === idx ? "active" : ""
                        }`}
                        onClick={() => setActive(t.talento_id, idx)}
                        type="button"
                      >
                        F{f.tipo_fortaleza ?? idx + 1}
                        {activeFrase === idx && (
                          <motion.div
                            className="cs-tab-indicator"
                            layoutId={`pt-tab-indicator-${t.talento_id}`}
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {frase && (
                    <motion.div
                      key={frase.frase_id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.22 }}
                    >
                      <div className="cs-question-box">
                        <div className="cs-question-icon">
                          <Icon icon="mdi:target" />
                        </div>
                        <p className="cs-question-text mb-0">
                          Fortaleza {frase.tipo_fortaleza} · {frase.frase}
                        </p>
                      </div>

                      <div className="cs-frases-list">
                        {frase.respuestas.length === 0 ? (
                          <div className="cs-empty-state">
                            <Icon icon="mdi:minus-circle-outline" />
                            <span>Sin respuestas</span>
                          </div>
                        ) : (
                          frase.respuestas.map((r, i) => (
                            <motion.div
                              key={r.sub_frase_id}
                              className={`pt-summary-row ${r.valor ? "si" : "no"}`}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.05, duration: 0.26 }}
                            >
                              <div className="pt-summary-row-check">
                                <Icon
                                  icon={
                                    r.valor
                                      ? "mdi:check-circle"
                                      : "mdi:close-circle"
                                  }
                                />
                              </div>
                              <span className="pt-summary-row-text">
                                {r.texto}
                              </span>
                            </motion.div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default PtTabPorTalento;
